import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { PaperPlaneIcon, TextToVoiceIcon } from '@rsos/assets/icons';
import { Absolute, Box, Loader, Text } from '@rsos/base-components';
import { InputStyles } from '@rsos/base-components/form/inputs/InputText/InputText.styles';
import Popover from '@rsos/components/capstone/base/Popover';
import { useFlag } from '@rsos/flags';
import useHasAccessToCommunicatorTextToVoiceTranslation from '../../../../../../hooks/useHasAccessToCommunicatorTextToVoiceTranslation';
import {
  selectTranscript,
  maximize,
  minimize,
  selectTranscriptionForCall,
} from '../transcriptionSlice';
import CopyTranscript from './CopyTranscript';
import LanguageChangeDivider from './LanguageChangeDivider';
import LanguagePillSelector from './LanguageSelector/LanguagePillSelector';
import LanguagePillSelectorV2 from './LanguageSelector/LanguagePillSelectorV2';
import getLanguageDisplayNames from './LanguageSelector/getLanguageDisplayNames';
import ManualLanguageSelector from './ManualLanguageSelector';
import MaximizeToggle from './MaximizeToggle';
import NewMessagesIndicator from './NewMessagesIndicator';
import TextToVoiceModal from './TextToVoiceModal/TextToVoiceModal';
import TextToVoiceToggle from './TextToVoiceToggle';
import TranscriptKeywordFilter from './TranscriptKeywordFilter';
import TranscriptTokenWithHighlighting from './TranscriptTokenWithHighlighting';
import {
  StyledTranscriptViewer,
  StyledTranscriptTitle,
  StyledTranscriptState,
  StyledTranscriptHeader,
  StyledSpeaker,
  StyledTimestamp,
  StyledMessage,
  StyledToken,
  StyledLoaderContainer,
  StyledTranscriptTokens,
  ButtonContainer,
  StyledHoverableText,
  PopoverBox,
  PopoverText,
  LanguageControlsWrapper,
} from './TranscriptViewer.styles';
import { TEXT_TO_VOICE_LANGUAGES_SET } from './constants';
import mergeTokens from './mergeTokens';
import { activateInput } from './textToVoiceSlice';
import {
  formatPhoneNumberSlice,
  callMidasTrigger,
} from './transcriptViewerUtils';

const TEXTAREA_MAX_HEIGHT = 104;

export const formatTimestamp = timestamp => {
  try {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('default', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return '';
  }
};

const Container = styled(Box).attrs({
  position: 'relative',
  height: '100%',
  overflowX: 'hidden',
  overflowY: 'auto',
  minHeight: '50px',
})`
  &:hover ${ButtonContainer} {
    transform: translateX(0);
    opacity: 1;
  }
`;

const TextToVoiceContainer = styled(Box).attrs({
  bg: 'foreground',
  border: '1px solid',
  borderColor: 'heavyLineOutline',
  width: '100%',
  borderRadius: '0px 0px 5px 5px',
})`
  border-top-width: 0;
`;

const TextArea = styled.textarea`
  ${InputStyles}
  resize: none;
  overflow: auto;
  max-height: ${TEXTAREA_MAX_HEIGHT}px;
  height: auto;
  // &::placeholder {
  //   padding: 8px;
  // }
  padding: 8px;
`;

const TranscriptToken = ({ token, selectedTranscript }) => {
  let transcript = token?.transcript;
  if (selectedTranscript === 'translated' && token?.translated) {
    transcript = token?.translated;
  }

  return (
    <StyledMessage>
      <StyledSpeaker
        speakerName={token?.speaker_name}
        data-name="transcript-token-speaker"
      >
        {token?.speaker_display_name}
      </StyledSpeaker>{' '}
      {token?.start_time && (
        <StyledTimestamp>{formatTimestamp(token?.start_time)}</StyledTimestamp>
      )}
      <StyledToken data-name="transcript-token-message">
        "{transcript}"
      </StyledToken>
    </StyledMessage>
  );
};

const TranscriptViewer = ({ callerId, isTranscriptMaximized = false }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [midasConferenceNumber, setMidasConferenceNumber] = useState(null);
  const [pinJobID, setPinJobID] = useState(null);
  const [textToVoiceInputValue, setTextToVoiceInputValue] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessagesWhileScrolledUp, setNewMessagesWhileScrolledUp] =
    useState(false);
  const [isPinTooltipVisible, setIsPinTooltipVisible] = useState(false);
  const [isPhoneTooltipVisible, setIsPhoneTooltipVisible] = useState(false);
  const [isPhoneNumberCopied, setIsPhoneNumberCopied] = useState(false);
  const [isPinCopied, setIsPinCopied] = useState(false);

  const languageDetectionHandled = useRef(false);
  const viewerRef = useRef(null);
  const pinPopoverRef = useRef();
  const phonePopoverRef = useRef();

  const dispatch = useDispatch();
  const selectedTranscript = useSelector(
    state => state.transcriptions.selectedTranscript,
  );
  const originalTranscription = useSelector(
    selectTranscriptionForCall(callerId),
  );
  const transcription = mergeTokens(originalTranscription);
  const languageChangeEvents =
    originalTranscription?.languageChangeEvents || [];
  const manualLanguage = originalTranscription?.manualLanguage;
  const hasSummary = useSelector(state => {
    const summaries = selectTranscriptionForCall(callerId)(state)?.summaries;
    return summaries && summaries.length > 0;
  });
  const isTextToVoiceSelected = useSelector(
    state => state?.textToVoice?.calls?.[callerId]?.visible,
  );

  const hasAccessToTextToVoiceTranslation =
    useHasAccessToCommunicatorTextToVoiceTranslation();

  const handleScroll = () => {
    if (viewerRef.current) {
      const BORDER_WIDTHS = 2;
      const { scrollTop, scrollHeight, clientHeight } = viewerRef.current;
      const atBottom = scrollTop + clientHeight >= scrollHeight - BORDER_WIDTHS;
      setIsAtBottom(atBottom);
      if (atBottom) {
        setNewMessagesWhileScrolledUp(false);
      }
    }
  };

  // Reattach listener whenever the transcription changes
  useEffect(() => {
    const attachScrollListener = () => {
      const viewer = viewerRef.current;
      if (viewer) {
        viewer.addEventListener('scroll', handleScroll);
      }
    };

    // Attach listener when the component mounts or when transcription changes
    attachScrollListener();

    return () => {
      const viewer = viewerRef.current;
      if (viewer) {
        viewer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [transcription]);

  // When changing between calls, resets state of notification for new messages, the status for the scroll, flag isAtBottom and language buttons
  useEffect(() => {
    dispatch(selectTranscript('original'));
    setNewMessagesWhileScrolledUp(false);
    if (viewerRef.current) {
      viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
    }
    setIsAtBottom(true);
  }, [callerId, dispatch]);

  // When transcription changes auto-scroll to the newest message.
  // NumTokens has been created to avoid a sporatic (and difficult to reproduce) bug where
  // transcription is sometimes undefined/null which causes a crash.
  const numTokens = transcription?.payload?.tokens?.length ?? 0;
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    if (isAtBottom && numTokens > 0) {
      // autoscroll only if user is already at the bottom
      viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
    } else if (!isAtBottom && numTokens > 0) {
      // if new messages arrive while not at the bottom, set the indicator state
      setNewMessagesWhileScrolledUp(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numTokens]);

  useEffect(() => {
    if (
      transcription?.payload?.languagesDetected?.length > 0 &&
      !languageDetectionHandled.current
    ) {
      dispatch(selectTranscript('translated'));
      languageDetectionHandled.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcription]);

  const { tokens = [] } = transcription?.payload || {};
  const detectedLanguages = transcription?.payload?.languagesDetected || [];
  const hasDetectedLanguages = detectedLanguages.length > 0;
  const isManualLanguageEnabled = useFlag('manual_language_selection');
  const hasManualLanguageChanges =
    isManualLanguageEnabled && languageChangeEvents.length > 0;
  const shouldShowLanguagePills =
    hasDetectedLanguages || hasManualLanguageChanges;

  const effectiveLanguages = useMemo(() => {
    const languages = new Set(detectedLanguages);

    if (isManualLanguageEnabled && languageChangeEvents.length > 0) {
      languageChangeEvents.forEach(event => {
        if (event.language && event.language !== 'en') {
          languages.add(event.language);
        }
      });
    }

    return Array.from(languages);
  }, [detectedLanguages, isManualLanguageEnabled, languageChangeEvents]);

  const tokensWithDividers = useMemo(() => {
    if (!isManualLanguageEnabled || !languageChangeEvents.length) {
      return tokens.map(token => ({ type: 'token', data: token }));
    }

    const combined = [
      ...tokens.map(token => ({
        type: 'token',
        data: token,
        sortTime: token.start_time,
        sequenceNumber: Infinity, // Tokens don't have sequence numbers, so they sort after events
      })),
      ...languageChangeEvents.map(event => ({
        type: 'languageChange',
        data: event,
        sortTime: event.timestamp,
        sequenceNumber: event.sequenceNumber ?? 0,
      })),
    ];

    // Sort by timestamp first, then by sequence number to maintain insertion order
    combined.sort((a, b) => {
      if (a.sortTime !== b.sortTime) {
        return a.sortTime - b.sortTime;
      }
      return (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0);
    });

    return combined;
  }, [tokens, languageChangeEvents, isManualLanguageEnabled]);

  let selectedTranscriptText = '';
  if (shouldShowLanguagePills) {
    selectedTranscriptText = 'Auto-translated to English';
    if (selectedTranscript === 'original') {
      const languageDisplayNames =
        getLanguageDisplayNames(effectiveLanguages) || [];
      selectedTranscriptText = `Original - ${languageDisplayNames.join(', ')}`;
    }
  }

  const handlePinHover = isVisible => setIsPinTooltipVisible(isVisible);
  const handlePinMouseEnter = () => handlePinHover(true);
  const handlePinMouseLeave = () => handlePinHover(false);

  const handlePhoneHover = isVisible => setIsPhoneTooltipVisible(isVisible);
  const handlePhoneMouseEnter = () => handlePhoneHover(true);
  const handlePhoneMouseLeave = () => handlePhoneHover(false);

  const handlePinCopy = () => {
    if (!navigator.clipboard) {
      console.error(
        'Copy feature unavailable. It is only available on a secure (HTTPS) connection.',
      );
      return;
    }
    navigator.clipboard.writeText(pinJobID || '');
    setIsPinCopied(true);
    setTimeout(() => {
      setIsPinCopied(false);
    }, 3000);
  };

  const handlePhoneNumberCopy = () => {
    if (!navigator.clipboard) {
      console.error(
        'Copy feature unavailable. It is only available on a secure (HTTPS) connection.',
      );
      return;
    }
    const massagedNumber = midasConferenceNumber.replace(/\D/g, '');
    navigator.clipboard.writeText(massagedNumber);
    setIsPhoneNumberCopied(true);
    setTimeout(() => {
      setIsPhoneNumberCopied(false);
    }, 3000);
  };

  const handleMaximize = () => {
    if (isTranscriptMaximized) {
      dispatch(minimize());
    } else {
      dispatch(maximize());
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      await handleSendMessage();
    } catch (err) {
      console.log(err);
    }
  };

  const callMidas = async payload => {
    const data = await callMidasTrigger(payload);

    if (!data) {
      setPinJobID(null);
      setMidasConferenceNumber(null);
      return;
    }

    if (!pinJobID && data.job_id) {
      setPinJobID(data.job_id);
    }

    if (!midasConferenceNumber && data.communicator_translator_phone) {
      setMidasConferenceNumber(
        formatPhoneNumberSlice(data.communicator_translator_phone),
      );
    }
  };

  const handleMidasPhoneNumber = async () => {
    if (midasConferenceNumber && pinJobID) {
      setIsModalVisible(true);
      return;
    }

    const payload = {
      callflow: 'communicator-translation',
      blocking: true,
      variables: {},
    };

    await callMidas(payload);
    setIsModalVisible(true);
    dispatch(activateInput(callerId));
  };

  async function handleSendMessage() {
    if (!textToVoiceInputValue || !hasDetectedLanguages) {
      return;
    }

    const languageCodes = getLanguageDisplayNames(detectedLanguages) || [];
    const targetLanguageCode = languageCodes[0]?.toLowerCase();

    if (!targetLanguageCode || targetLanguageCode === 'english') {
      return;
    }

    const payload = {
      callflow: 'communicator-translation',
      blocking: true,
      variables: {
        job_id: pinJobID,
        message: textToVoiceInputValue,
        target_language: targetLanguageCode,
      },
    };

    setTextToVoiceInputValue('');

    await callMidas(payload);
  }

  const isKeywordSetsEnabled = useFlag('keyword_sets');

  const supportsLanguages = useMemo(
    () =>
      detectedLanguages.length > 0 &&
      detectedLanguages.every(language =>
        TEXT_TO_VOICE_LANGUAGES_SET.has(language),
      ),
    [detectedLanguages],
  );

  return (
    <>
      <Container>
        <StyledTranscriptViewer
          data-name="transcript-viewer"
          isTextToVoiceSelected={isTextToVoiceSelected}
        >
          <StyledTranscriptHeader
            pb={tokens.length > 0 && !shouldShowLanguagePills ? '12px' : '0px'}
          >
            <Box>
              <StyledTranscriptTitle noMargin>
                Call Audio Transcript
              </StyledTranscriptTitle>
            </Box>
            <ButtonContainer>
              {hasAccessToTextToVoiceTranslation &&
                tokens.length > 0 &&
                supportsLanguages && (
                  <TextToVoiceToggle
                    disabled={false}
                    isSelected={isTextToVoiceSelected}
                    onClick={handleMidasPhoneNumber}
                  />
                )}
              <CopyTranscript
                selectedTranscript={selectedTranscript}
                transcription={transcription}
              />
              {tokens.length > 0 && (
                <MaximizeToggle
                  disabled={!hasSummary}
                  isMaximized={isTranscriptMaximized}
                  onClick={handleMaximize}
                />
              )}
            </ButtonContainer>
          </StyledTranscriptHeader>

          {isManualLanguageEnabled ? (
            <LanguageControlsWrapper>
              <ManualLanguageSelector callerId={callerId} />
              {shouldShowLanguagePills ? (
                <LanguagePillSelectorV2
                  selectedLanguage={selectedTranscript}
                  detectedLanguages={effectiveLanguages}
                  onChange={transcriptVersion => {
                    dispatch(selectTranscript(transcriptVersion));
                  }}
                />
              ) : (
                <div style={{ flex: 1 }} />
              )}
            </LanguageControlsWrapper>
          ) : (
            shouldShowLanguagePills && (
              <LanguagePillSelector
                selectedLanguage={selectedTranscript}
                detectedLanguages={effectiveLanguages}
                onChange={transcriptVersion => {
                  dispatch(selectTranscript(transcriptVersion));
                }}
              />
            )
          )}

          {isKeywordSetsEnabled && <TranscriptKeywordFilter />}
          {transcription?.fetching && !tokens.length ? (
            <StyledLoaderContainer>
              <Loader noText aria-label="Loading transcript" />
            </StyledLoaderContainer>
          ) : !tokens.length ? (
            <StyledTranscriptState>
              Conversation has not started yet
            </StyledTranscriptState>
          ) : (
            <StyledTranscriptTokens
              data-name="transcript-viewer-tokens"
              ref={viewerRef}
            >
              {tokensWithDividers.map(item => {
                if (item.type === 'languageChange') {
                  return (
                    <LanguageChangeDivider
                      key={`lang-change-${item.data.timestamp}`}
                      language={item.data.language}
                      isManual={item.data.isManual}
                    />
                  );
                }

                const token = item.data;
                return isKeywordSetsEnabled ? (
                  <TranscriptTokenWithHighlighting
                    key={token.token_id}
                    token={token}
                    selectedTranscript={selectedTranscript}
                  />
                ) : (
                  <TranscriptToken
                    key={token?.token_id}
                    token={token}
                    selectedTranscript={selectedTranscript}
                  />
                );
              })}
            </StyledTranscriptTokens>
          )}
        </StyledTranscriptViewer>

        {tokens.length !== 0 && newMessagesWhileScrolledUp && !isAtBottom && (
          <Absolute
            left="50%"
            bottom={isTextToVoiceSelected ? '120px' : '16px'}
            style={{
              // shift the indicator to the left by half of its width to center it
              transform: 'translate(-50%, 0)',
            }}
          >
            <NewMessagesIndicator
              onClick={() => {
                if (!viewerRef.current) return;
                viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
                setIsAtBottom(true);
                setNewMessagesWhileScrolledUp(false);
              }}
            />
          </Absolute>
        )}
        {isTextToVoiceSelected && (
          <TextToVoiceContainer>
            <Box
              horizontal
              py="8px"
              px="12px"
              justify="space-between"
              align="center"
              gridColumnGap="8px"
            >
              <Box horizontal align="center" gridColumnGap="8px">
                <TextToVoiceIcon width={16} height={16} color="secondaryText" />
                <Text size="normal" color="primaryText" fontWeight="semibold">
                  Text-to-Voice Translation
                </Text>
                <Popover
                  cssDisplayProperty="inline-block"
                  data-name="pin-tooltip"
                  isOpen={isPinTooltipVisible}
                  popoverID="pin-tooltip"
                  trigger={
                    <StyledHoverableText
                      size="normal"
                      color="primaryText"
                      data-id="pin-text"
                      onClick={handlePinCopy}
                      onMouseEnter={handlePinMouseEnter}
                      onMouseLeave={handlePinMouseLeave}
                    >
                      PIN {pinJobID}
                    </StyledHoverableText>
                  }
                  useCenterXOffset
                  yOffset={58}
                >
                  <PopoverBox ref={pinPopoverRef}>
                    <PopoverText>{isPinCopied ? 'Copied' : 'Copy'}</PopoverText>
                  </PopoverBox>
                </Popover>
              </Box>
              <Popover
                cssDisplayProperty="inline-block"
                data-name="phone-tooltip"
                isOpen={isPhoneTooltipVisible}
                popoverID="phone-tooltip"
                trigger={
                  <StyledHoverableText
                    size="normal"
                    color="secondaryText"
                    data-id="phone-number-text"
                    onClick={handlePhoneNumberCopy}
                    onMouseEnter={handlePhoneMouseEnter}
                    onMouseLeave={handlePhoneMouseLeave}
                  >
                    Dial {midasConferenceNumber}
                  </StyledHoverableText>
                }
                useCenterXOffset
                yOffset={58}
              >
                <PopoverBox ref={phonePopoverRef}>
                  <PopoverText>
                    {isPhoneNumberCopied ? 'Copied' : 'Copy'}
                  </PopoverText>
                </PopoverBox>
              </Popover>
            </Box>
            <Box
              horizontal
              width="100%"
              borderTop="1px solid"
              borderColor="heavyLineOutline"
              padding="12px"
            >
              <TextArea
                placeholder="Type your response and HARMONY will translate it"
                value={textToVoiceInputValue}
                onChange={e => setTextToVoiceInputValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    handleSubmit(e);
                  }
                }}
              />
              <Box
                p="16px"
                onClick={handleSubmit}
                color="primaryText"
                cursor="pointer"
              >
                <PaperPlaneIcon width={16} height={16} color="secondaryText" />
              </Box>
            </Box>
          </TextToVoiceContainer>
        )}
      </Container>
      <TextToVoiceModal
        isOpened={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        pinJobID={pinJobID}
        midasConferenceNumber={midasConferenceNumber}
      />
    </>
  );
};

export default TranscriptViewer;
